"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import SectionHeading from "@/components/SectionHeading";
import { 
  Award, 
  Code, 
  Users, 
  Clock,
  TrendingUp,
  Zap,
  Target,
  Star
} from "lucide-react";

interface ImpactMetric {
  value: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  color: string;
  subMetrics?: { label: string; value: string }[];
}

const impactMetrics: ImpactMetric[] = [
  {
    value: "80+",
    label: "Workflows Automated",
    icon: <Code className="w-6 h-6" />,
    description: "Complex business processes transformed into intelligent, self-running systems",
    color: "from-blue-500 to-cyan-500",
    subMetrics: [
      { label: "Industries Served", value: "12+" },
      { label: "Avg. Complexity", value: "High" },
      { label: "Success Rate", value: "100%" }
    ]
  },
  {
    value: "250K+",
    label: "Hours Saved",
    icon: <Clock className="w-6 h-6" />,
    description: "Time returned to teams for strategic, creative, and meaningful work",
    color: "from-green-500 to-emerald-500",
    subMetrics: [
      { label: "Per Month", value: "15K+" },
      { label: "Per Client Avg", value: "3.2K" },
      { label: "Fastest ROI", value: "2 weeks" }
    ]
  },
  {
    value: "6.5×",
    label: "Average ROI",
    icon: <TrendingUp className="w-6 h-6" />,
    description: "Typical return on investment with payback period under 3 months",
    color: "from-purple-500 to-pink-500",
    subMetrics: [
      { label: "Best ROI", value: "12×" },
      { label: "Avg. Payback", value: "2.8 mo" },
      { label: "Cost Reduction", value: "65%" }
    ]
  },
  {
    value: "99.9%",
    label: "Accuracy Rate",
    icon: <Target className="w-6 h-6" />,
    description: "Precision in automated processes, eliminating human error",
    color: "from-orange-500 to-red-500",
    subMetrics: [
      { label: "Error Reduction", value: "95%" },
      { label: "Quality Score", value: "A+" },
      { label: "Uptime", value: "99.8%" }
    ]
  }
];

const achievements = [
  {
    icon: <Award className="w-5 h-5 text-yellow-500" />,
    title: "Industry Recognition",
    description: "Featured in tech publications for innovative AI automation solutions"
  },
  {
    icon: <Users className="w-5 h-5 text-blue-500" />,
    title: "Global Client Base",
    description: "Serving SMEs across North America, Europe, and Asia-Pacific"
  },
  {
    icon: <Zap className="w-5 h-5 text-purple-500" />,
    title: "Rapid Implementation",
    description: "Average deployment time of 2-4 weeks for complex workflows"
  },
  {
    icon: <Star className="w-5 h-5 text-green-500" />,
    title: "Client Satisfaction",
    description: "100% client retention rate with ongoing optimization services"
  }
];

const testimonialHighlight = {
  quote: "Rehman didn't just automate our processes—he transformed how we think about work. Our team now focuses on strategy instead of spreadsheets.",
  author: "Sarah Chen",
  role: "Operations Director",
  company: "TechFlow Solutions",
  impact: "Saved 40 hours/week, 300% productivity increase"
};

const ImpactShowcase = () => {
  return (
    <section id="impact" className="py-24 px-6 bg-gradient-to-b from-secondary/30 to-background">
      <div className="container max-w-7xl mx-auto">
        <SectionHeading title="The Numbers Don&apos;t Lie" />
        
        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-xl text-muted-foreground leading-relaxed max-w-4xl mx-auto">
            Real impact, measured results. Here&apos;s what happens when you transform 
            manual chaos into intelligent automation.
          </p>
        </motion.div>

        {/* Main Impact Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {impactMetrics.map((metric, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group relative"
            >
              <div className="bg-card border border-border rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                {/* Header */}
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-r ${metric.color} text-white`}>
                    {metric.icon}
                  </div>
                  <div>
                    <motion.div
                      initial={{ scale: 0.8 }}
                      whileInView={{ scale: 1 }}
                      transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
                      viewport={{ once: true }}
                      className="text-3xl font-bold"
                    >
                      {metric.value}
                    </motion.div>
                    <div className="text-sm font-medium text-muted-foreground">
                      {metric.label}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  {metric.description}
                </p>

                {/* Sub-metrics */}
                {metric.subMetrics && (
                  <div className="space-y-2 pt-4 border-t border-border">
                    {metric.subMetrics.map((subMetric, subIndex) => (
                      <div key={subIndex} className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">{subMetric.label}</span>
                        <span className="text-xs font-semibold">{subMetric.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Personal Story & Image */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h3 className="text-3xl font-bold">Why These Numbers Matter</h3>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Behind every statistic is a team that got their evenings back, a business owner 
              who can finally focus on growth, and employees who remember why they loved their 
              jobs in the first place.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              I don&apos;t just build automation—I build freedom. Freedom from repetitive tasks, 
              freedom from human error, and freedom to focus on what truly matters: innovation, 
              creativity, and human connection.
            </p>
            
            {/* Skills Highlight */}
            <div className="flex flex-wrap gap-2">
              {["AI Engineering", "LLM Integration", "Full-Stack Development", "Cloud Architecture", "Team Leadership"].map((skill, index) => (
                <motion.span 
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="px-3 py-1 bg-accent/10 text-accent rounded-full text-sm font-medium"
                >
                  {skill}
                </motion.span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative w-full max-w-md mx-auto">
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-primary/20 rounded-3xl -rotate-3" />
              <div className="absolute inset-0 bg-gradient-to-tl from-primary/20 to-accent/20 rounded-3xl rotate-2" />
              
              {/* Image */}
              <div className="relative w-full aspect-square rounded-3xl overflow-hidden border-2 border-border bg-card shadow-xl">
                <Image 
                  src="/rehman.webp" 
                  alt="Abdul Rehman working on AI automation" 
                  fill
                  className="object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Achievements Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-center mb-8">Recognition & Achievements</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-xl p-4 text-center hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-3 flex justify-center">
                  <div className="p-2 bg-muted rounded-lg">
                    {achievement.icon}
                  </div>
                </div>
                <h4 className="font-semibold mb-2">{achievement.title}</h4>
                <p className="text-sm text-muted-foreground">{achievement.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Testimonial Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-accent/10 via-primary/5 to-accent/10 rounded-3xl p-8 border border-accent/20 text-center"
        >
          <blockquote className="text-2xl md:text-3xl font-bold mb-6">
            &ldquo;{testimonialHighlight.quote}&rdquo;
          </blockquote>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <div className="text-center md:text-left">
              <div className="font-semibold">{testimonialHighlight.author}</div>
              <div className="text-sm text-muted-foreground">
                {testimonialHighlight.role}, {testimonialHighlight.company}
              </div>
            </div>
            <div className="hidden md:block w-px h-12 bg-border" />
            <div className="text-center">
              <div className="text-sm font-medium text-accent">{testimonialHighlight.impact}</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ImpactShowcase; 